// اپ کارکنان: قاب مشترک (i18n، Theme RTL، Query، OIDC) + Router با Routeهای پوسته.
import { AppFrame, type AuthClient } from '@phoenix/auth';
import type { QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createBrowserRouter, createMemoryRouter, RouterProvider } from 'react-router';
import { isPublicPath } from './navigation';
import { createStaffRoutes, type StaffRouteOptions } from './routes';

export interface StaffAppProps extends StaffRouteOptions {
  authClient: AuthClient;
  /** Router حافظه‌ای برای تست (مسیرهای اولیه). */
  memoryEntries?: string[];
  queryClient?: QueryClient;
}

/**
 * Router پس از آماده‌سازی AuthProvider ساخته می‌شود تا نشانی بازگشت ورود (returnTo، مثلاً /invitations/:token یا /account)
 * که init در history می‌نشاند همان مسیر اولیه Router باشد (C-AGENT-220).
 */
function StaffRouter({
  memoryEntries,
  routeOptions,
}: {
  memoryEntries?: string[];
  routeOptions: StaffRouteOptions;
}) {
  const [router] = useState(() => {
    const routes = createStaffRoutes(routeOptions);
    return memoryEntries
      ? createMemoryRouter(routes, { initialEntries: memoryEntries })
      : createBrowserRouter(routes);
  });
  return <RouterProvider router={router} />;
}

export function StaffApp({ authClient, memoryEntries, queryClient, ...routeOptions }: StaffAppProps) {
  // مسیرهای عمومی بدون ورود (پذیرش دعوت، بازیابی رمز)؛ در تست مسیر اولیه Router حافظه‌ای.
  const [allowAnonymous] = useState(() =>
    memoryEntries
      ? () => isPublicPath(new URL(memoryEntries[0] ?? '/', 'http://localhost').pathname)
      : (pathname: string) => isPublicPath(pathname),
  );

  return (
    <AppFrame authClient={authClient} queryClient={queryClient} allowAnonymous={allowAnonymous}>
      <StaffRouter memoryEntries={memoryEntries} routeOptions={routeOptions} />
    </AppFrame>
  );
}
